import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';

import Header from '../../common/Header';
import Footer from '../../common/Footer';
import SeoHelmet from '../../common/SeoHelmet';
const USCSClassifierTheory = () => {
  return (
    <>
      <Header />
      <SeoHelmet
        title="GeoCalc | USCS Soil Classification — ASTM D2487-17 | Theory"
        description="Overview of the Unified Soil Classification System (USCS) per ASTM D2487-17: coarse vs. fine soils, Atterberg-limit based groups, organic soils, and practical limitations."
        url="https://www.geocalc.app/uscsClassifier-theory"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="page"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'USCS Classification | Theory', url: 'https://www.geocalc.app/uscsClassifier-theory' },
        ]}
      />
      <div className="theory">
        <div className="theoryContainer uscsClassifierTheory">
          <h1>USCS Soil Classification System (ASTM D2487-17)</h1>

          <h2>Introduction</h2>
          <p>
            The Unified Soil Classification System (USCS) is a widely used method for grouping soils with similar engineering
            behavior. It provides a standardized way of describing soils based on grain-size distribution and plasticity
            characteristics. The classification is primarily used in geotechnical engineering practice to help predict soil
            performance in foundations, embankments, pavements, and other civil engineering applications.
          </p>

          <h2>General Principles</h2>
          <p>
            The USCS divides soils into two major categories: <strong>coarse-grained soils</strong>, which contain more than 50%
            retained on the No. 200 (0.075 mm) sieve, and <strong>fine-grained soils</strong>, which contain more than 50% passing
            the No. 200 sieve. A third category, <strong>highly organic soils</strong>, is used for materials with significant
            organic content or very high compressibility.
          </p>

          <h2>Classification of Coarse-Grained Soils</h2>
          <p>
            Coarse-grained soils are further divided into <strong>gravels</strong> (more than 50% retained on the No. 4 sieve) and{' '}
            <strong>sands</strong> (more than 50% smaller than the No. 4 sieve). Within each group, gradation characteristics
            (well-graded vs. poorly graded) and the amount of fines (material passing the No. 200 sieve) are considered.
          </p>

          <h2>Classification of Fine-Grained Soils</h2>
          <p>
            Fine-grained soils (silts and clays) are classified primarily based on the <strong>liquid limit (LL)</strong> and{' '}
            <strong>plasticity index (PI)</strong>, which are determined from Atterberg limits testing. These parameters reflect
            the soil’s consistency and its behavior with changes in moisture content. Fine soils are grouped into low-, medium-,
            or high-plasticity silts (ML, MH) and clays (CL, CH).
          </p>

          <h2>Organic Soils</h2>
          <p>
            Soils with significant organic matter or peat-like characteristics are placed in the organic groups (OL, OH, Pt).
            These soils typically exhibit high compressibility and low strength, and require special consideration in geotechnical
            design.
          </p>

          <h2>Use and Limitations</h2>
          <p>
            The USCS provides a common language for soil description but does not directly predict engineering properties such as
            shear strength or compressibility. Additional laboratory or field testing is usually required for design. The system
            is not intended for classifying soils with unusual properties, such as cemented soils or industrial byproducts.
          </p>

          <h2>Reference</h2>
          <p>
            This classification system is based on the methodology described in{' '}
            <strong>
              ASTM D2487 – 17: Standard Practice for Classification of Soils for Engineering Purposes (Unified Soil Classification
              System)
            </strong>
            . For detailed procedures and official requirements, consult the ASTM standard directly.
          </p>
        </div>
        <div className="theoryButtonContainer">
          <Link to={'/calculations/uscsClassifier'}>
            <Button variant="outlined" size="small">
              GO TO USCS SOIL CLASSIFIER
            </Button>
          </Link>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default USCSClassifierTheory;
